
import { useState, useEffect, useRef, useCallback } from 'react';

export function useLocalStorage<T,>(key: string, initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const initialValueRef = useRef(initialValue);
  
  // Fonction utilitaire pour lire depuis localStorage de manière sécurisée
  const readValue = useCallback(() => {
    if (typeof window === 'undefined') {
      return initialValueRef.current;
    }
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValueRef.current;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValueRef.current;
    }
  }, [key]);

  const [storedValue, setStoredValue] = useState<T>(readValue);
  const prevKeyRef = useRef(key);

  // Gérer le changement de clé (ex: déconnexion puis connexion avec un autre utilisateur) de manière asynchrone pour éviter les bugs si `setStoredValue` est appelé pendant le rendu par erreur avant que state ne soit défini.
  useEffect(() => {
    if (prevKeyRef.current !== key) {
      prevKeyRef.current = key;
      setStoredValue(readValue());
    }
  }, [key, readValue]);

  // Retourner une fonction setStoredValue qui met a jour l'etat ET le localStorage en mm tps
  const setValue: React.Dispatch<React.SetStateAction<T>> = useCallback((value) => {
    try {
      setStoredValue((prev) => {
        // Allow value to be a function so we have same API as useState
        const valueToStore = value instanceof Function ? value(prev) : value;
        // Save to local storage
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }
        return valueToStore;
      });
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  }, [key]);

  return [storedValue, setValue];
}
